import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Chip from '@mui/material/Chip';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LoadingRow from '../../../components/TableBox/Rows/LoadingRow';
import Button from '../../../components/Button';
import Toast from '../../../components/Toast';
import DashboardService, { DashboardActionItems } from '../../../services/dashboard.service';
import TenantService from '../../../services/tenant.service';
import { Modules } from '../../../enums';

const POLL_INTERVAL_MS = 60_000;

type SectionKey = 'expiringSoon' | 'expired' | 'pendingPayment' | 'mandatoryConfigMissing' | 'failedProvisioning';

const SECTION_SEVERITY: Record<SectionKey, 'warning' | 'error'> = {
  expiringSoon: 'warning',
  expired: 'error',
  pendingPayment: 'warning',
  mandatoryConfigMissing: 'warning',
  failedProvisioning: 'error',
};

const RenewalRadar: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [actionItems, setActionItems] = useState<DashboardActionItems | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [retryingId, setRetryingId] = useState<number | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback((silent: boolean) => {
    if (!silent) setLoading(true);
    DashboardService.getActionItems()
      .then((response: any) => {
        setActionItems(response?.data ?? null);
        setLastUpdated(new Date());
      })
      .finally(() => {
        if (!silent) setLoading(false);
      });
  }, []);

  useEffect(() => {
    load(false);
    pollRef.current = setInterval(() => load(true), POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [load]);

  useEffect(() => {
    if (!location.hash) return;
    const el = document.getElementById(location.hash.replace('#', ''));
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [location.hash, actionItems]);

  const handleRetry = useCallback(
    async (tenantId: number) => {
      setRetryingId(tenantId);
      try {
        await TenantService.retryProvisioning(tenantId);
        toast.success(<Toast message={t('renewalRadar.pageTexts.failedProvisioning.retrySuccess')} />, { icon: false });
        load(true);
      } finally {
        setRetryingId(null);
      }
    },
    [load, t]
  );

  const goToTenant = useCallback((tenantId: number) => navigate(`${Modules.tenants}/${tenantId}`), [navigate]);

  if (loading || !actionItems) {
    return (
      <div className="obs-table-card card mt-3">
        <div className="card-body p-0" style={{ overflowX: 'auto' }}>
          <table className="table obs-table mb-0">
            <tbody>
              <LoadingRow colSpan={4} />
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  const sections: { key: SectionKey; title: string; count: number }[] = [
    { key: 'expiringSoon', title: t('renewalRadar.pageTexts.sections.expiringSoon'), count: actionItems.expiringSoon.count },
    { key: 'expired', title: t('renewalRadar.pageTexts.sections.expired'), count: actionItems.expired.count },
    { key: 'pendingPayment', title: t('renewalRadar.pageTexts.sections.pendingPayment'), count: actionItems.pendingPayment.count },
    {
      key: 'mandatoryConfigMissing',
      title: t('renewalRadar.pageTexts.sections.mandatoryConfigMissing'),
      count: actionItems.mandatoryConfigMissing.count,
    },
    {
      key: 'failedProvisioning',
      title: t('renewalRadar.pageTexts.sections.failedProvisioning'),
      count: actionItems.failedProvisioning.count,
    },
  ];

  return (
    <div>
      <div className="mb-4 d-flex justify-content-between align-items-end">
        <div>
          <h4 className="m-0">{t('renewalRadar.pageTexts.title')}</h4>
          <p className="text-muted m-0">{t('renewalRadar.pageTexts.subTitle')}</p>
        </div>
        {lastUpdated && (
          <span className="text-muted small">
            {t('renewalRadar.pageTexts.lastUpdated', { time: lastUpdated.toLocaleTimeString() })}
          </span>
        )}
      </div>

      {sections.map((section) => (
        <Accordion key={section.key} id={`section-${section.key}`} defaultExpanded={section.count > 0}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <div className="d-flex align-items-center gap-2 w-100">
              <span className="fw-medium">{section.title}</span>
              {section.count > 0 ? (
                <Chip
                  size="small"
                  label={section.count}
                  color={SECTION_SEVERITY[section.key] === 'error' ? 'error' : 'warning'}
                />
              ) : (
                <Chip size="small" label={t('renewalRadar.pageTexts.allClear')} color="success" variant="outlined" />
              )}
            </div>
          </AccordionSummary>
          <AccordionDetails>
            {section.key === 'expiringSoon' && (
              <ItemList
                items={actionItems.expiringSoon.items}
                empty={t('renewalRadar.pageTexts.allClear')}
                renderRow={(item) => (
                  <Row key={item.tenantId} onClick={() => goToTenant(item.tenantId)}>
                    <span className="fw-medium">{item.tenantName}</span>
                    <span className="text-muted small">
                      {item.planName} — {t('renewalRadar.pageTexts.expiringSoon.daysUntil', { count: item.daysUntilExpiry })}
                    </span>
                  </Row>
                )}
              />
            )}

            {section.key === 'expired' && (
              <ItemList
                items={actionItems.expired.items}
                empty={t('renewalRadar.pageTexts.allClear')}
                renderRow={(item) => (
                  <Row key={item.tenantId} onClick={() => goToTenant(item.tenantId)}>
                    <span className="fw-medium">{item.tenantName}</span>
                    <span className="text-muted small">
                      {t('renewalRadar.pageTexts.expired.daysOverdue', { count: item.daysOverdue })}
                    </span>
                  </Row>
                )}
              />
            )}

            {section.key === 'pendingPayment' && (
              <ItemList
                items={actionItems.pendingPayment.items}
                empty={t('renewalRadar.pageTexts.allClear')}
                renderRow={(item) => (
                  <Row key={item.tenantId} onClick={() => goToTenant(item.tenantId)}>
                    <span className="fw-medium">{item.tenantName}</span>
                    <span className="text-muted small">
                      {item.currency} {item.amount}
                    </span>
                  </Row>
                )}
              />
            )}

            {section.key === 'mandatoryConfigMissing' && (
              <ItemList
                items={actionItems.mandatoryConfigMissing.items}
                empty={t('renewalRadar.pageTexts.allClear')}
                renderRow={(item) => (
                  <Row key={item.tenantId} onClick={() => goToTenant(item.tenantId)}>
                    <span className="fw-medium">{item.tenantName}</span>
                    <span className="text-muted small">
                      {t('renewalRadar.pageTexts.mandatoryConfigMissing.missingCount', { count: item.missingCount })}
                    </span>
                  </Row>
                )}
              />
            )}

            {section.key === 'failedProvisioning' && (
              <ItemList
                items={actionItems.failedProvisioning.items}
                empty={t('renewalRadar.pageTexts.allClear')}
                renderRow={(item) => (
                  <Row key={item.tenantId}>
                    <div className="d-flex flex-column cursor-pointer" onClick={() => goToTenant(item.tenantId)}>
                      <span className="fw-medium">{item.tenantName}</span>
                      <span className="text-muted small">{item.errorMessage ?? '—'}</span>
                    </div>
                    <Button
                      className="btn btn-sm btn-outline-danger"
                      text={t('renewalRadar.pageTexts.failedProvisioning.retry')}
                      loading={retryingId === item.tenantId}
                      onClick={() => handleRetry(item.tenantId)}
                    />
                  </Row>
                )}
              />
            )}
          </AccordionDetails>
        </Accordion>
      ))}
    </div>
  );
};

function ItemList<T>({
  items,
  empty,
  renderRow,
}: {
  items: T[];
  empty: string;
  renderRow: (item: T) => React.ReactNode;
}) {
  if (items.length === 0) {
    return <div className="text-muted">{empty}</div>;
  }
  return <div className="d-flex flex-column gap-2">{items.map(renderRow)}</div>;
}

const Row: React.FC<{ onClick?: () => void; children: React.ReactNode }> = ({ onClick, children }) => (
  <div
    className={`d-flex justify-content-between align-items-center border-bottom pb-2 ${onClick ? 'cursor-pointer' : ''}`}
    onClick={onClick}
  >
    {children}
  </div>
);

export default RenewalRadar;
